import React, { memo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Select, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	INetworkElementsAutoSwitchDTO,
	TNetworkElementAutoSwitchDTO,
} from '@interfaces/networkElements/dto/networkElements';
import { INetworkElement } from '@interfaces/networkElements/networkElements';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { nonNullishMinutesOptions } from '@constants/timeOptions';
import {
	AUTOSWITCH_TIME_INTERVAL_LABEL,
	NETCONF_CHECK_PERIOD_LABEL,
} from '@components/routes/AdminPanel/ManageNetworkElements/constants';
import {
	ENetworkElementsFields,
} from '@components/routes/AdminPanel/ManageNetworkElements/NetworkElementsForm/constants';

import { autoSwitchSchema } from './schema';

import styles from './NetworkElementsAutoSwitchForm.module.scss';

const SELECT_MIN_WIDTH = '80px';
const SELECT_MAX_HEIGHT = 100;

interface INetworkElementsAutoSwitchFormForm {
	onSubmit: (dto: TNetworkElementAutoSwitchDTO) => void;
	onClose: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	networkElement?: INetworkElement;
	id: string;
	isLoadingSubmit?: boolean;
}

const NetworkElementsAutoSwitchForm = ({
	onSubmit,
	onClose,
	setIsDirtyForm,
	networkElement,
	isLoadingSubmit,
	id,
}: INetworkElementsAutoSwitchFormForm) => {
	const {
		formState: { errors, isDirty },
		handleSubmit,
		control,
	} = useForm<INetworkElementsAutoSwitchDTO<TSelectOption<number>>>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(autoSwitchSchema),
		defaultValues: {
			lastVesTimePeriod: {
				label: networkElement?.lastVesTimePeriod,
				value: networkElement?.lastVesTimePeriod,
			},
			netconfCheckTimePeriod: {
				label: networkElement?.netconfCheckTimePeriod,
				value: networkElement?.netconfCheckTimePeriod,
			},
		},
	});

	useEffect(() => {
		setIsDirtyForm(isDirty);
	}, [isDirty, setIsDirtyForm]);

	const submitHandler = useCallback((values: INetworkElementsAutoSwitchDTO<TSelectOption<number>>) => {
		const { lastVesTimePeriod, netconfCheckTimePeriod } = values;

		onSubmit({
			lastVesTimePeriod: lastVesTimePeriod!.value!,
			netconfCheckTimePeriod: netconfCheckTimePeriod!.value!,
			id,
		});
	}, [onSubmit, id]);

	return (
		<div className={styles.container}>
			{isLoadingSubmit && <SpinnerOverlay />}
			<Select
				className={styles.wrapper}
				control={control}
				errorMessage={errors.lastVesTimePeriod?.message}
				isError={!!errors?.lastVesTimePeriod}
				isOverflowLabelText
				label={AUTOSWITCH_TIME_INTERVAL_LABEL}
				maxMenuHeight={SELECT_MAX_HEIGHT}
				minWidth={SELECT_MIN_WIDTH}
				name={ENetworkElementsFields.lastVesTimePeriod}
				options={nonNullishMinutesOptions}
				required
				units='min'
			/>
			<Select
				className={styles.wrapper}
				control={control}
				errorMessage={errors.netconfCheckTimePeriod?.message}
				isError={!!errors?.netconfCheckTimePeriod}
				isOverflowLabelText
				label={NETCONF_CHECK_PERIOD_LABEL}
				maxMenuHeight={SELECT_MAX_HEIGHT}
				minWidth={SELECT_MIN_WIDTH}
				name={ENetworkElementsFields.netconfCheckTimePeriod}
				options={nonNullishMinutesOptions}
				required
				units='min'
			/>
			<SubmissionButtons
				disabledPositive={isLoadingSubmit}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(submitHandler)}
				positiveLabel='Proceed'
			/>
		</div>
	);
};

export default memo(NetworkElementsAutoSwitchForm);
